import type { FFmpeg } from '@ffmpeg/ffmpeg';
import type { fetchFile as FfmpegFetchFile, toBlobURL as ToBlobUrlFn } from '@ffmpeg/util';

// Max payload we want to send to server to avoid FUNCTION_PAYLOAD_TOO_LARGE on Vercel (≈4.5MB limit)
// Target final compressed size between 4MB and 4.4MB to maximize quality while staying below the limit.
export const TARGET_MIN_BYTES = 4 * 1024 * 1024; // 4.0 MB
export const MAX_UPLOAD_BYTES = Math.floor(4.4 * 1024 * 1024); // ≈4.4 MB

const FFMPEG_CORE_VERSION = '0.12.10';
const FFMPEG_CORE_CDN_BASES = [
  `https://cdn.jsdelivr.net/npm/@ffmpeg/core@${FFMPEG_CORE_VERSION}/dist/umd`,
  `https://cdn.jsdelivr.net/npm/@ffmpeg/core@${FFMPEG_CORE_VERSION}/dist/esm`,
  `https://unpkg.com/@ffmpeg/core@${FFMPEG_CORE_VERSION}/dist/umd`,
  `https://unpkg.com/@ffmpeg/core@${FFMPEG_CORE_VERSION}/dist/esm`,
] as const;

type FetchFileFn = typeof FfmpegFetchFile;
type ToBlobUrl = typeof ToBlobUrlFn;

type FfmpegRuntime = {
  ffmpeg: FFmpeg;
  fetchFile: FetchFileFn;
};

type FfmpegAssetUrls = {
  coreURL: string;
  wasmURL: string;
};

let ffmpegRuntimeCache: FfmpegRuntime | null = null;
let ffmpegRuntimePromise: Promise<FfmpegRuntime> | null = null;
let ffmpegLock: Promise<void> = Promise.resolve();
let ffmpegAssetUrlsCache: FfmpegAssetUrls | null = null;
let ffmpegAssetUrlsPromise: Promise<FfmpegAssetUrls> | null = null;

async function resolveFfmpegAssetUrls(toBlobURL: ToBlobUrl): Promise<FfmpegAssetUrls> {
  if (ffmpegAssetUrlsCache) return ffmpegAssetUrlsCache;
  if (!ffmpegAssetUrlsPromise) {
    ffmpegAssetUrlsPromise = (async () => {
      for (const base of FFMPEG_CORE_CDN_BASES) {
        try {
          const sanitizedBase = base.replace(/\/$/, '');
          const [coreURL, wasmURL] = await Promise.all([
            toBlobURL(`${sanitizedBase}/ffmpeg-core.js`, 'text/javascript'),
            toBlobURL(`${sanitizedBase}/ffmpeg-core.wasm`, 'application/wasm'),
          ]);

          const assets: FfmpegAssetUrls = { coreURL, wasmURL };
          ffmpegAssetUrlsCache = assets;
          return assets;
        } catch (error) {
          console.warn(`[fileCompression] Failed to load ffmpeg.wasm assets from ${base}:`, error);
        }
      }
      throw new Error('Unable to load ffmpeg.wasm assets from configured CDNs');
    })().catch(error => {
      ffmpegAssetUrlsPromise = null;
      throw error;
    });
  }
  return ffmpegAssetUrlsPromise;
}

async function withFfmpegLock<T>(fn: () => Promise<T>): Promise<T> {
  let release!: () => void;
  const wait = ffmpegLock;
  ffmpegLock = new Promise<void>(resolve => {
    release = resolve;
  });
  await wait;
  try {
    return await fn();
  } finally {
    release();
  }
}

async function ensureFfmpegRuntime(): Promise<FfmpegRuntime> {
  // Ensure we're in a browser environment before attempting anything
  if (typeof window === 'undefined') {
    throw new Error('ffmpeg.wasm requires a browser environment');
  }

  if (ffmpegRuntimeCache) return ffmpegRuntimeCache;
  if (ffmpegRuntimePromise) return ffmpegRuntimePromise;

  ffmpegRuntimePromise = (async () => {
    try {
      // Use dynamic imports to avoid breaking server-side rendering
      const [{ FFmpeg }, { fetchFile, toBlobURL }] = await Promise.all([
        import('@ffmpeg/ffmpeg'),
        import('@ffmpeg/util'),
      ]);

      if (!FFmpeg || typeof fetchFile !== 'function') {
        console.warn('[fileCompression] ffmpeg.wasm or @ffmpeg/util exports are invalid');
        throw new Error('ffmpeg.wasm module exports are invalid');
      }

      const ffmpeg = new FFmpeg();
      ffmpeg.on('log', ({ type, message }) => {
        // This can be very verbose. Enable for debugging if needed.
        // console.log(`[ffmpeg] ${type}: ${message}`);
      });
      
      // Load ffmpeg
      if (!ffmpeg.loaded) {
        const assetUrls = await resolveFfmpegAssetUrls(toBlobURL);
        await ffmpeg.load(assetUrls);
      }

      const runtime: FfmpegRuntime = { ffmpeg, fetchFile };
      ffmpegRuntimeCache = runtime;
      return runtime;
    } catch (err) {
      console.warn('[fileCompression] Video compression unavailable:', err instanceof Error ? err.message : err);
      throw new Error('ffmpeg.wasm entrypoints missing');
    }
  })();

  try {
    return await ffmpegRuntimePromise;
  } catch (error) {
    ffmpegRuntimePromise = null;
    ffmpegRuntimeCache = null;
    throw error;
  }
}

/**
 * Compress an image file client-side using Canvas API
 * Returns a compressed File or null if compression fails
 */
export async function compressImageClientSide(file: File): Promise<File | null> {
  try {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Iteratively downscale and reduce quality
    const resizeScales = [1, 0.95, 0.9, 0.85, 0.8, 0.75, 0.7, 0.6, 0.5, 0.4, 0.3];
    const qualitySteps = [0.98, 0.92, 0.88, 0.82, 0.76, 0.7, 0.64, 0.58, 0.5, 0.4, 0.3];

    let bestCandidateFile: File | null = null;
    let bestCandidateSize = 0;

    outer:
    for (const scale of resizeScales) {
      canvas.width = Math.max(1, Math.floor(bitmap.width * scale));
      canvas.height = Math.max(1, Math.floor(bitmap.height * scale));
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

      for (const q of qualitySteps) {
        // Prefer WebP where supported
        const mime = 'image/webp';
        const blob: Blob | null = await new Promise(resolve => canvas.toBlob(b => resolve(b), mime, q));
        if (blob) {
          const size = blob.size;
          if (size <= MAX_UPLOAD_BYTES) {
            const base = file.name.includes('.') ? file.name.substring(0, file.name.lastIndexOf('.')) : file.name;
            const candidate = new File([blob], `${base}.webp`, { type: mime, lastModified: Date.now() });
            if (size >= TARGET_MIN_BYTES) {
              return candidate;
            }
            if (!bestCandidateFile || size > bestCandidateSize) {
              bestCandidateFile = candidate;
              bestCandidateSize = size;
            }
            break outer;
          }
        }

        // Try JPEG as fallback at same quality
        const blobJ: Blob | null = await new Promise(resolve => canvas.toBlob(b => resolve(b), 'image/jpeg', q));
        if (blobJ) {
          const sizeJ = blobJ.size;
          if (sizeJ <= MAX_UPLOAD_BYTES) {
            const base = file.name.includes('.') ? file.name.substring(0, file.name.lastIndexOf('.')) : file.name;
            const candidateJ = new File([blobJ], `${base}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
            if (sizeJ >= TARGET_MIN_BYTES) {
              return candidateJ;
            }
            if (!bestCandidateFile || sizeJ > bestCandidateSize) {
              bestCandidateFile = candidateJ;
              bestCandidateSize = sizeJ;
            }
            break outer;
          }
        }
      }
    }

    if (bestCandidateFile) return bestCandidateFile;

    const blobMin: Blob | null = await new Promise(resolve => canvas.toBlob(b => resolve(b), 'image/webp', 0.3));
    if (blobMin && blobMin.size <= MAX_UPLOAD_BYTES) {
      const base = file.name.includes('.') ? file.name.substring(0, file.name.lastIndexOf('.')) : file.name;
      return new File([blobMin], `${base}.webp`, { type: 'image/webp', lastModified: Date.now() });
    }
    return null;
  } catch (e) {
    console.warn('[fileCompression] Client image compression failed:', e);
    return null;
  }
}

/**
 * Compress a video file client-side using ffmpeg.wasm
 * Returns a compressed File or null if compression fails
 */
export async function compressVideoClientSide(file: File): Promise<File | null> {
  try {
    const runtime = await ensureFfmpegRuntime();

    const compressed = await withFfmpegLock(async () => {
      const { ffmpeg, fetchFile } = runtime;

      const inputName = 'input.mp4';
      const attemptOutputs = Array.from({ length: 11 }, (_, i) => `out_${i}.mp4`);

      await ffmpeg.writeFile(inputName, await fetchFile(file));

      const attempts = [
        { width: 1280, vbr: 3500, abr: 128 },
        { width: 1280, vbr: 2500, abr: 128 },
        { width: 960, vbr: 2000, abr: 112 },
        { width: 854, vbr: 1500, abr: 96 },
        { width: 720, vbr: 1300, abr: 96 },
        { width: 640, vbr: 1100, abr: 80 },
        { width: 540, vbr: 900, abr: 64 },
        { width: 480, vbr: 700, abr: 64 },
        { width: 360, vbr: 550, abr: 48 },
        { width: 320, vbr: 420, abr: 48 },
        { width: 256, vbr: 320, abr: 32 },
      ];

      let bestCandidate: File | null = null;
      let bestCandidateSize = 0;

      try {
        for (let i = 0; i < attempts.length; i++) {
          const { width, vbr, abr } = attempts[i];
          const outputName = attemptOutputs[i];
          try {
            await ffmpeg.deleteFile(outputName);
          } catch {/* ignore */}

          const args = [
            '-i', inputName,
            '-vf', `scale='min(${width},iw)':-2`,
            '-c:v', 'libx264',
            '-preset', 'veryfast',
            '-b:v', `${vbr}k`,
            '-maxrate', `${vbr}k`,
            '-bufsize', `${vbr * 2}k`,
            '-movflags', '+faststart',
            '-c:a', 'aac',
            '-b:a', `${abr}k`,
            '-ac', '2',
            '-y', outputName,
          ];
          await ffmpeg.exec(args);
          const out = await ffmpeg.readFile(outputName);
          if (out && out.length > 0) {
            const size = out.length;
            if (size <= MAX_UPLOAD_BYTES) {
              const base = file.name.includes('.') ? file.name.substring(0, file.name.lastIndexOf('.')) : file.name;
              const candidate = new File([out], `${base}.mp4`, { type: 'video/mp4', lastModified: Date.now() });
              if (size >= TARGET_MIN_BYTES) {
                return candidate;
              }
              if (!bestCandidate || size > bestCandidateSize) {
                bestCandidate = candidate;
                bestCandidateSize = size;
              }
              break;
            }
          }
        }

        // Return the smallest produced if still too big
        const lastName = attemptOutputs[attemptOutputs.length - 1];
        try {
          const last = await ffmpeg.readFile(lastName);
          if (last && last.length > 0) {
            const base = file.name.includes('.') ? file.name.substring(0, file.name.lastIndexOf('.')) : file.name;
            return new File([last], `${base}.mp4`, { type: 'video/mp4', lastModified: Date.now() });
          }
        } catch {/* ignore */}
        if (bestCandidate) return bestCandidate;
        return null;
      } finally {
        try {
          await ffmpeg.deleteFile(inputName);
        } catch {/* ignore */}
        for (const name of attemptOutputs) {
          try {
            await ffmpeg.deleteFile(name);
          } catch {/* ignore */}
        }
      }
    });

    return compressed;
  } catch (e) {
    console.warn('[fileCompression] Client video compression unavailable/failed:', e);
    return null;
  }
}

/**
 * Upload a file directly to Azure Blob Storage using SAS URL
 * Returns the blob URL
 */
export async function uploadViaAzureBlob(file: File, userId: string): Promise<string> {
  // 1) Get SAS URL for this file
  const params = new URLSearchParams({ 
    filename: file.name, 
    user: userId, 
    contentType: file.type || 'application/octet-stream' 
  });
  const sasResp = await fetch(`/api/storage/sas?${params.toString()}`);
  if (!sasResp.ok) throw new Error(await sasResp.text().catch(() => 'Failed to get SAS'));
  const { sasUrl, blobUrl } = await sasResp.json() as { sasUrl: string; blobUrl: string };

  // 2) Upload to blob using PUT
  const putResp = await fetch(sasUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'x-ms-version': '2020-10-02',
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });
  if (!putResp.ok) throw new Error(await putResp.text().catch(() => 'Azure PUT failed'));

  // Return the blobUrl (server route will fetch it)
  return blobUrl;
}

